import { Plus, Minus, Trash2, ShoppingBag, MessageCircle, X, Sparkles, Truck } from "lucide-react";
import { useState } from "react";
import { useCart, FREE_SHIPPING_THRESHOLD } from "../../contexts/CartContext";

function formatPrice(price) {
  return new Intl.NumberFormat("fr-FR").format(price) + " FCFA";
}

function CartItem({ item }) {
  const { updateQuantity, removeItem } = useCart();
  const [imgError, setImgError] = useState(false);
  return (
    <div
      data-testid={`cart-item-${item.id}`}
      className="flex gap-3 p-3 rounded-2xl bg-white border border-stone-200/70 hover:border-[#6C3FC5]/30 transition-colors"
    >
      <div className="w-20 h-20 rounded-xl bg-stone-50 overflow-hidden flex-shrink-0">
        {!imgError ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#6C3FC5]/10 to-[#6C3FC5]/5 text-[#6C3FC5] font-bold">
            {item.short_name}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-2">
          <h4
            className="font-semibold text-stone-900 text-sm truncate"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {item.name}
          </h4>
          <button
            onClick={() => removeItem(item.id)}
            data-testid={`cart-remove-${item.id}`}
            aria-label="Supprimer"
            className="text-stone-400 hover:text-red-500 transition-colors p-0.5"
          >
            <Trash2 size={15} />
          </button>
        </div>
        <p className="text-xs text-stone-500 mb-2">{item.short_name}</p>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 bg-stone-100 rounded-full p-0.5">
            <button
              onClick={() => updateQuantity(item.id, item.quantity - 1)}
              data-testid={`cart-decrease-${item.id}`}
              className="w-7 h-7 rounded-full bg-white hover:bg-[#6C3FC5] hover:text-white text-stone-700 transition-colors flex items-center justify-center"
              aria-label="Diminuer"
            >
              <Minus size={12} />
            </button>
            <span
              data-testid={`cart-qty-${item.id}`}
              className="text-sm font-semibold text-stone-900 min-w-[20px] text-center"
            >
              {item.quantity}
            </span>
            <button
              onClick={() => updateQuantity(item.id, item.quantity + 1)}
              data-testid={`cart-increase-${item.id}`}
              className="w-7 h-7 rounded-full bg-white hover:bg-[#6C3FC5] hover:text-white text-stone-700 transition-colors flex items-center justify-center"
              aria-label="Augmenter"
            >
              <Plus size={12} />
            </button>
          </div>
          <div className="text-right">
            <p className="text-[#6C3FC5] font-bold text-sm">
              {formatPrice(item.price * item.quantity)}
            </p>
            {item.original_price > 0 && item.original_price > item.price && (
              <p className="text-[10px] text-stone-400 line-through">
                {formatPrice(item.original_price * item.quantity)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CartDrawer() {
  const {
    items,
    isOpen,
    setIsOpen,
    totalItems,
    totalPrice,
    totalSavings,
    remainingForFreeShipping,
    freeShippingUnlocked,
    buildWhatsAppOrder,
    clearCart,
  } = useCart();

  const progress = Math.min(100, (totalPrice / FREE_SHIPPING_THRESHOLD) * 100);
  const orderUrl = buildWhatsAppOrder();

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setIsOpen(false)}
        className={`fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-[60] transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#F5F0E8] z-[70] shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-label="Panier"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#6C3FC5]/10 flex items-center justify-center text-[#6C3FC5]">
              <ShoppingBag size={18} />
            </div>
            <div>
              <h2
                className="font-bold text-stone-900 text-lg leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Mon panier
              </h2>
              <p className="text-xs text-stone-500">
                {totalItems} {totalItems > 1 ? "articles" : "article"}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            data-testid="cart-close-btn"
            aria-label="Fermer"
            className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Free shipping progress */}
        {items.length > 0 && (
          <div className="px-5 py-3 bg-white border-b border-stone-200">
            {freeShippingUnlocked ? (
              <div className="flex items-center gap-2 text-[#10B981] text-sm font-medium">
                <Sparkles size={16} />
                <span>Livraison offerte débloquée !</span>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs text-stone-600 mb-1.5">
                  <span className="flex items-center gap-1.5">
                    <Truck size={13} className="text-[#6C3FC5]" />
                    Plus que <strong className="text-[#6C3FC5]">{formatPrice(remainingForFreeShipping)}</strong> pour la livraison gratuite
                  </span>
                </div>
                <div className="h-2 rounded-full bg-stone-200 overflow-hidden">
                  <div
                    data-testid="free-shipping-progress"
                    className="h-full bg-gradient-to-r from-[#6C3FC5] to-[#9F6FFF] rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-20 h-20 rounded-full bg-[#6C3FC5]/10 flex items-center justify-center text-[#6C3FC5] mb-4">
                <ShoppingBag size={32} />
              </div>
              <h3
                className="font-bold text-stone-900 text-lg mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Votre panier est vide
              </h3>
              <p className="text-stone-500 text-sm mb-6">
                Ajoutez nos compléments alimentaires premium pour commencer.
              </p>
              <button
                onClick={() => setIsOpen(false)}
                data-testid="cart-continue-shopping"
                className="bg-[#6C3FC5] hover:bg-[#582FB0] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all hover:scale-105"
              >
                Découvrir les produits
              </button>
            </div>
          ) : (
            items.map((item) => <CartItem key={item.id} item={item} />)
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-stone-200 bg-white px-5 py-4 space-y-3">
            {totalSavings > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-600 flex items-center gap-1.5">
                  <Sparkles size={14} className="text-[#10B981]" />
                  Économies
                </span>
                <span className="text-[#10B981] font-semibold" data-testid="cart-savings">
                  -{formatPrice(totalSavings)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-stone-700 font-medium">Total</span>
              <span
                data-testid="cart-total"
                className="text-[#6C3FC5] font-bold text-xl"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {formatPrice(totalPrice)}
              </span>
            </div>
            <a
              href={orderUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cart-checkout-whatsapp"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-3.5 rounded-full text-sm font-semibold transition-all hover:scale-[1.02] shadow-lg shadow-[#25D366]/25"
            >
              <MessageCircle size={18} />
              Commander via WhatsApp
            </a>
            <button
              onClick={clearCart}
              data-testid="cart-clear-btn"
              className="w-full text-xs text-stone-500 hover:text-red-500 transition-colors"
            >
              Vider le panier
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
